import React, { Fragment, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants';

interface SavedPost {
    id: string;
    postId: string;
    post: {
        id: string;
        title: string;
        content?: string;
        mediaUrls: string[];
        mediaTypes: string[];
        createdAt: string;
        user: {
            id: string;
            username?: string;
            email?: string;
            avatar?: string;
        };
    };
    savedAt: string;
}

interface Collection {
    id: string;
    name: string;
    description?: string;
    postsCount: number;
    createdAt: string;
}

const CollectionsPage: React.FC = () => {
    const [collections, setCollections] = useState<Collection[]>([]);
    const [savedPosts, setSavedPosts] = useState<SavedPost[]>([]);
    const [activeTab, setActiveTab] = useState<'all' | 'collections'>('all');
    const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showCreateCollection, setShowCreateCollection] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState('');
    const [newCollectionDescription, setNewCollectionDescription] = useState('');
    const { user, token } = useAuth();

    useEffect(() => {
        fetchSavedContent();
    }, []);

    const fetchSavedContent = async () => {
        try {
            // For MVP, show placeholder since saved content system is not yet implemented
            setSavedPosts([]);
            setCollections([]);
        } catch (error) {
            console.error('Error fetching saved content:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUnsavePost = async (postId: string) => {
        // Placeholder for unsaving posts
        console.log('Unsaving post:', postId);
    };

    const handleCreateCollection = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newCollectionName.trim()) return;

        // Placeholder for creating collections
        console.log('Creating collection:', newCollectionName);
        setShowCreateCollection(false);
        setNewCollectionName('');
        setNewCollectionDescription('');
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getMediaPreview = (post: SavedPost['post']) => {
        if (!post.mediaUrls || post.mediaUrls.length === 0) {
            return (
                <div className="bg-greylight d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
                    <i className="feather-file-text font-lg text-grey-500"></i>
                </div>
            );
        }

        const firstMedia = post.mediaUrls[0];
        const firstMediaType = post.mediaTypes[0];

        if (firstMediaType?.startsWith('image/')) {
            return (
                <img
                    src={firstMedia}
                    alt="Post preview"
                    className="w-100"
                    style={{ height: '150px', objectFit: 'cover' }}
                />
            );
        } else {
            return (
                <video
                    src={firstMedia}
                    className="w-100"
                    style={{ height: '150px', objectFit: 'cover' }}
                />
            );
        }
    };

    return (
        <Fragment>
            <div className="container-fluid py-2">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-xss w-100 d-block d-flex border-0 p-4 mb-3">
                            <div className="card-body d-flex align-items-center justify-content-between p-0">
                                <div>
                                    <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900">Collections</h2>
                                    <p className="text-grey-500 font-xsss mb-0">Manage your saved posts and collections</p>
                                </div>
                                <button
                                    className="btn bg-current text-white font-xsss fw-600 p-2 rounded-3"
                                    onClick={() => setShowCreateCollection(true)}
                                >
                                    <i className="feather-plus me-2"></i>
                                    New Collection
                                </button>
                            </div>
                        </div>

                        <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
                            <div className="card-body p-0">
                                <ul className="nav nav-tabs h55 d-flex product-info-tab border-bottom-0 ps-4" role="tablist">
                                    <li className="active list-inline-item me-5">
                                        <a
                                            className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'all' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('all')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            All Saved ({savedPosts.length})
                                        </a>
                                    </li>
                                    <li className="list-inline-item me-5">
                                        <a
                                            className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'collections' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('collections')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Collections ({collections.length})
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {loading ? (
                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {activeTab === 'all' && (
                                    <>
                                        {savedPosts.length === 0 ? (
                                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                                <div className="text-center p-4">
                                                    <i className="feather-bookmark font-xl text-grey-500 mb-3"></i>
                                                    <h4 className="fw-700 text-grey-900">No saved posts yet</h4>
                                                    <p className="text-grey-500 font-xsss">Save posts to view them here later</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                {savedPosts.map((savedPost) => (
                                                    <div key={savedPost.id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                                        <div className="card shadow-xss w-100 d-block border-0 p-0 mb-0">
                                                            <div className="card-body p-0">
                                                                {getMediaPreview(savedPost.post)}
                                                                <div className="p-3">
                                                                    <div className="d-flex align-items-center mb-2">
                                                                        <img
                                                                            src={savedPost.post.user.avatar || '/assets/images/user.png'}
                                                                            alt="Creator"
                                                                            className="rounded-circle me-2"
                                                                            style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                                                        />
                                                                        <span className="font-xsss fw-600 text-grey-700">
                                                                            {savedPost.post.user.username || savedPost.post.user.email?.split('@')[0]}
                                                                        </span>
                                                                        <button
                                                                            className="btn p-0 ms-auto text-grey-500"
                                                                            onClick={() => handleUnsavePost(savedPost.postId)}
                                                                            title="Remove from saved"
                                                                        >
                                                                            <i className="feather-bookmark-x font-sm"></i>
                                                                        </button>
                                                                    </div>
                                                                    <h4 className="fw-700 font-xsss text-grey-900 mb-1">
                                                                        {savedPost.post.title}
                                                                    </h4>
                                                                    {savedPost.post.content && (
                                                                        <p className="font-xsss text-grey-500 mb-2 text-truncate">
                                                                            {savedPost.post.content}
                                                                        </p>
                                                                    )}
                                                                    <span className="font-xssss text-grey-500">
                                                                        Saved {formatDate(savedPost.savedAt)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'collections' && (
                                    <>
                                        {collections.length === 0 ? (
                                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                                <div className="text-center p-4">
                                                    <i className="feather-folder font-xl text-grey-500 mb-3"></i>
                                                    <h4 className="fw-700 text-grey-900">No collections yet</h4>
                                                    <p className="text-grey-500 font-xsss">Create collections to organize your saved posts</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                {collections.map((collection) => (
                                                    <div key={collection.id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                                        <div className="card shadow-xss w-100 d-block border-0 p-4 cursor-pointer">
                                                            <div className="card-body p-0">
                                                                <div className="d-flex align-items-center mb-3">
                                                                    <div className="btn-round-lg bg-lightblue me-3">
                                                                        <i className="feather-folder font-md text-current"></i>
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <h4 className="fw-700 font-xsss text-grey-900 mb-0">
                                                                            {collection.name}
                                                                        </h4>
                                                                        <span className="font-xssss text-grey-500">
                                                                            {collection.postsCount} posts
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                {collection.description && (
                                                                    <p className="font-xsss text-grey-500 mb-2">
                                                                        {collection.description}
                                                                    </p>
                                                                )}
                                                                <span className="font-xssss text-grey-500">
                                                                    Created {formatDate(collection.createdAt)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Collection Modal */}
            {showCreateCollection && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0">
                            <div className="modal-header">
                                <h5 className="modal-title fw-700">Create New Collection</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowCreateCollection(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleCreateCollection}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label font-xsss fw-600">Collection Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newCollectionName}
                                            onChange={(e) => setNewCollectionName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label font-xsss fw-600">Description (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={newCollectionDescription}
                                            onChange={(e) => setNewCollectionDescription(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn bg-grey text-white font-xsss fw-600"
                                        onClick={() => setShowCreateCollection(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn bg-current text-white font-xsss fw-600"
                                    >
                                        Create Collection
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default CollectionsPage;